import React, { useState, useCallback, useEffect } from "react"
import { CircularProgress, AutocompleteProps } from "@mui/material"
import { useGetLocationRegistersForOptionsLazyQuery } from "src/graphql/quark/quark.type"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import { useTypedSelector } from "src/shared/store/store"
import { WatchQueryFetchPolicy } from "@apollo/client"

interface Option {
  id: string
  name: string
}

const SelectRegisterField: React.FC<
  {
    onChange: (val: string | string[]) => void
    value?: string | string[] | null
    multiple?: boolean
    error?: boolean
    showChip?: "outside" | "inside"
    isAllRegisters?: boolean
    helperText?: string
    /** When set, loads registers for this location only (default: current warehouse). */
    locationId?: string
    /** When set with one or more ids, loads and merges registers for each location (deduped). Overrides `locationId`. */
    locationIds?: string[]
    /** When set, only registers whose id appears here are offered. */
    includeIds?: string[]
    label?: string
    placeholder?: string
    required?: boolean
    startAdornment?: React.ReactNode
    fetchPolicy?: WatchQueryFetchPolicy
  } & Omit<AutocompleteProps<any, any, any, any>, "renderInput" | "options">
> = ({
  onChange,
  value,
  error,
  multiple,
  helperText,
  locationId,
  locationIds,
  includeIds,
  isAllRegisters,
  showChip,
  label,
  placeholder,
  required,
  startAdornment,
  fetchPolicy,
  ...rest
}) => {
  const auth = useTypedSelector((state) => state.authentication)
  const [options, setOptions] = useState<Option[]>([])
  const [loading, setLoading] = useState(false)
  const [getLocationRegisters, { error: getError }] = useGetLocationRegistersForOptionsLazyQuery()

  useEffect(() => {
    if (value && !options.length) fetchOptions()
  }, [value])
  const fetchOptions = useCallback(async () => {
    setLoading(true)
    try {
      if (locationIds?.length) {
        const responses = await Promise.all(
          locationIds.map((locId) =>
            getLocationRegisters({
              fetchPolicy,
              variables: { locationId: locId, page: 1, limit: 100000 }
            })
          )
        )
        const seen = new Set<string>()
        const merged: Option[] = []
        for (const response of responses) {
          for (const val of response.data?.getLocationRegisters ?? []) {
            const id = val.id as string
            if (seen.has(id)) continue
            seen.add(id)
            merged.push({ id, name: val.name as string })
          }
        }
        merged.sort((a, b) => a.name.localeCompare(b.name))
        setOptions(merged)
        return
      }

      const response = await getLocationRegisters({
        fetchPolicy,
        variables: {
          ...(!isAllRegisters && { locationId: locationId ?? (auth.currentWhLoc?.id as string) }),
          page: 1,
          limit: 100000
        }
      })

      if (!response.data?.getLocationRegisters.length) {
        setOptions([])
        return
      }
      const resData: Option[] = response.data.getLocationRegisters.map((val: any) => ({
        id: val.id as string,
        name: val.name as string
      }))

      setOptions(resData)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }, [fetchPolicy, getLocationRegisters, locationId, locationIds, auth.currentWhLoc?.id])

  const visibleOptions = includeIds ? options.filter((o) => includeIds.includes(o.id)) : options

  return (
    <InputAutocomplete
      {...rest}
      options={visibleOptions}
      multiple={multiple}
      getOptionKey={(option) => option?.id ?? ""}
      getOptionLabel={(option) => option.name ?? ""}
      onOpen={() => fetchOptions()}
      loading={loading}
      value={
        multiple
          ? visibleOptions.filter((val) => value?.includes(val.id))
          : (visibleOptions.find((val) => val.id === value) ?? null)
      }
      onChange={(val: any) => {
        onChange(multiple ? val?.map((val: any) => val.id) : val?.id)
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name}
        </li>
      )}
      showChip={showChip}
      label={label}
      placeholder={placeholder}
      required={required}
      startAdornment={startAdornment}
      loadingText={<CircularProgress size={20} />}
      error={error ?? !!getError?.message}
      helperText={helperText ?? getError?.message}
    />
  )
}

export default SelectRegisterField
